import { gql } from '@apollo/client';
import { SocialPostFragmentUseInstagramStoryPostStateFragment } from 'graphql/generated';
import moment from 'moment';
import { theme } from 'styles/theme';

gql`
  fragment SocialPostFragmentUseInstagramStoryPostState on SocialPostModel {
    id
    expiresAt
  }
`;

export type InstagramStoryPostState = {
  isPostAStory: boolean;
  hasExpired: boolean;
  isExpiringSoon: boolean;
  // Pill bg color can be:
  // 1. rgba(35, 6, 3, 0.1) for normal
  // 2. gray-600 for expired
  // 3. pink-3 for expiring soon
  // Design reference: https://www.figma.com/design/WUgUR4zqaesDyom02Y2G4b/Community-Hub?node-id=2667-55246&t=0yWgxtFOjDeNAmbT-4
  pillBgColor: string;
  // show "Expired" if the story has expired, otherwise show the diff time from now in hours like "3h ago", "23h ago"
  timeAgo: string;
  // show the time left for the story to expire like "5h left", "1h left"
  timeLeft: string;
};

export type InstagramStoryPostStateProps = {
  socialPost?: SocialPostFragmentUseInstagramStoryPostStateFragment;
};

export const useInstagramStoryPostState = (
  props: InstagramStoryPostStateProps,
): InstagramStoryPostState => {
  const { socialPost } = props;

  if (!socialPost) {
    return {
      isPostAStory: false,
      hasExpired: false,
      isExpiringSoon: false,
      pillBgColor: 'rgba(35, 6, 3, 0.1)',
      timeAgo: '',
      timeLeft: '',
    };
  }

  const isPostAStory = !!socialPost.expiresAt;
  const hasExpired =
    isPostAStory && new Date(socialPost.expiresAt) < new Date();
  const isExpiringSoon =
    isPostAStory && moment(socialPost.expiresAt).diff(moment(), 'hours') <= 5;

  const pillBgColor = hasExpired
    ? `${theme.colors?.utility[600]} !important`
    : isExpiringSoon
    ? `${theme.colors?.utility['pink-3']} !important`
    : 'rgba(35, 6, 3, 0.1)';

  const timeAgo = hasExpired
    ? 'Expired'
    : `${-moment(socialPost.expiresAt)
        .subtract(1, 'day')
        .diff(moment(), 'hours')}h ago`;

  const timeLeft = isExpiringSoon
    ? `${moment(socialPost.expiresAt).diff(moment(), 'hours')}h left`
    : '';

  return {
    isPostAStory,
    hasExpired,
    isExpiringSoon,
    pillBgColor,
    timeAgo,
    timeLeft,
  };
};
